/**
 * @fileoverview Things done to a whole bed at once: archiving or deleting its plants at the end of a
 * season, and one note ("Fed") for every plant in it.
 *
 * Both open a panel over the same tick list, every plant ticked: untick
 * whatever stays in the ground, or should not get the note. Nothing is
 * written until the panel's button is pressed; clearing then asks Archive or
 * delete in the shared ArchiveOrDeleteDialog, and the toast after an archive
 * or a note can take it back.
 *
 * Where: panels LocationGrid opens from a bed card's ⋯ menu ("A note for
 * every plant", "Clear the bed") and from the bed dialog's "Archive all".
 * API: none of its own; LocationGrid's callbacks do POST
 * /locations/:id/notes or POST /locations/:id/plants/status; deleting is
 * ArchiveOrDeleteDialog's (DELETE /plants/:id for each plant).
 * Settings: PANEL_FOCUS_DELAY_MS (config/tunables/garden); the gardener's
 * quick note chips (QuickNoteChips: GET /note-shortcuts).
 * Words (en/es): locations.bed.* (clearTitle, clearBody, clearKeep,
 * remove, remove_one, archiveOrDelete, archiveOrDelete_one, nothing, cancel, failed, noteTitle, noteGoes,
 * noteGoes_one, noteKeep); plants.notes.saveFailed.
 * Feet (B111): the Clear the bed panel's foot is ActionRow (Cancel left, the action right, both 44px); the note panel's foot is NoteBox's.
 * Failures: said in the panel through errorMessage: the server's refusal,
 * or for a failure on our side (D31) the standard sentence, reported to
 * Sentry, with the support address as a link.
 *
 * @module BedActions
 */

import React from 'react';
import { Check } from 'lucide-react';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { LocationPlant } from '@/shared/api/locations';
import { cn } from '@/lib/utils';
import { NoteBox } from '@/features/plant-list/components/NoteBox';
import { QuickNoteChips } from '@/shared/components/QuickNoteChips';
import { withShortcut } from '@/shared/lib/noteShortcut';
import { PANEL_FOCUS_DELAY_MS } from '@/config/tunables/garden';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { ArchiveOrDeleteDialog, type ManyPlants } from '@/features/plant-list/components/ArchiveOrDeleteDialog';

/**
 * What a plant is called in the tick list: its one name (the backend's names,
 * services/grow/plantNaming).
 *
 * @param plant - One of the bed's plants.
 */
const nameOf = (plant: LocationPlant): string => plant.names.full;

/**
 * The bed's plants, each a whole-row tick. Scrolls inside the panel when the bed is big.
 *
 * @param props.plants - The bed's plants growing now.
 * @param props.ticked - The ids ticked.
 * @param props.onToggle - Ticks or unticks one.
 */
const PlantTicks: React.FC<{
  plants: LocationPlant[];
  ticked: Set<number>;
  onToggle: (plantObjId: number) => void;
}> = ({ plants, ticked, onToggle }) => (
  <ul className="max-h-[40dvh] overflow-y-auto rounded-sm border divide-y">
    {plants.map((plant) => {
      const on = ticked.has(plant.plant_obj_id);
      return (
        <li key={plant.plant_obj_id}>
          <button
            type="button"
            role="checkbox"
            aria-checked={on}
            onClick={() => onToggle(plant.plant_obj_id)}
            className="flex min-h-11 w-full items-center gap-3 px-3 text-left text-sm hover:bg-muted"
          >
            <span className={cn(
              'flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border-2 border-primary',
              on ? 'bg-primary text-primary-foreground' : 'bg-card',
            )}>
              {on && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
            </span>
            <span className="min-w-0 flex-1 break-words">{nameOf(plant)}</span>
          </button>
        </li>
      );
    })}
  </ul>
);

/**
 * Which of the bed's plants are ticked, every one to begin with and again
 * each time the panel opens.
 *
 * @param plants - The bed's plants.
 * @param open - Whether the panel is open.
 * @returns The ticked ids, and a toggle for one.
 */
function useTicks(plants: LocationPlant[], open: boolean): [Set<number>, (id: number) => void] {
  // Keyed on the ids, not the array, which the card rebuilds on every render.
  const ids = plants.map((p) => p.plant_obj_id).join(',');
  const [ticked, setTicked] = React.useState<Set<number>>(() => new Set(plants.map((p) => p.plant_obj_id)));
  // Every plant ticked again each time the panel opens.
  React.useEffect(() => {
    if (open) setTicked(new Set(ids ? ids.split(',').map(Number) : []));
  }, [open, ids]);
  /**
   * Ticks or unticks one plant.
   *
   * @param id - The plant's plant_obj_id.
   */
  const toggle = (id: number): void => setTicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  return [ticked, toggle];
}

/**
 * "Clear Squash Bed?": the ticked plants are archived or deleted for good,
 * asked in the shared ArchiveOrDeleteDialog; the bed stays.
 *
 * @param props.open - Whether the panel is up.
 * @param props.onOpenChange - Opens or closes it.
 * @param props.bedName - The bed's name, for the title.
 * @param props.plants - The bed's plants growing now.
 * @param props.onConfirm - Archives the ticked ones (with the caller's toast and Undo); a rejection keeps the dialog open with a message.
 * @param props.onArchiveQuietly - Archives some without a toast, the first step of deleting them.
 * @param props.onDeleted - Reads the beds and plants again after plants were deleted.
 */
export const ClearBedPanel: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bedName: string;
  plants: LocationPlant[];
  onConfirm: (plantObjIds: number[]) => Promise<void>;
  onArchiveQuietly: (plantObjIds: number[]) => Promise<void>;
  onDeleted: () => Promise<void>;
}> = ({ open, onOpenChange, bedName, plants, onConfirm, onArchiveQuietly, onDeleted }) => {
  const { reactTranslate: t } = useTranslation();
  const [ticked, toggle] = useTicks(plants, open);
  /** The Archive or delete question is up, for the plants ticked when it was opened. */
  const [asking, setAsking] = React.useState<ManyPlants | null>(null);
  React.useEffect(() => { if (!open) setAsking(null); }, [open]);

  const count = ticked.size;
  /** Opens the question for the ticked plants, by id and name as ticked now. */
  const ask = (): void => {
    setAsking({
      plants: plants.filter((p) => ticked.has(p.plant_obj_id)).map((p) => ({ plantObjId: p.plant_obj_id, name: nameOf(p) })),
      archiveAll: onConfirm,
      archiveQuietly: onArchiveQuietly,
      onDeleted,
    });
  };

  return (
    <>
      <BottomPanel open={open} onOpenChange={onOpenChange} title={t('locations.bed.clearTitle', { name: bedName })} wrapTitle>
        <p className="text-sm text-muted-foreground">
          {t('locations.bed.clearBody')} <span className="font-semibold text-foreground">{t('locations.bed.clearKeep')}</span>
        </p>
        <PlantTicks plants={plants} ticked={ticked} onToggle={toggle} />
        <ActionRow onCancel={() => onOpenChange(false)} cancelLabel={t('locations.bed.cancel')}>
          <Button onClick={ask} disabled={count === 0}>
            {count === 0
              ? t('locations.bed.nothing')
              : t(count === 1 ? 'locations.bed.archiveOrDelete_one' : 'locations.bed.archiveOrDelete', { count })}
          </Button>
        </ActionRow>
      </BottomPanel>
      <ArchiveOrDeleteDialog
        plant={null}
        many={asking}
        onClose={() => setAsking(null)}
        archive={() => Promise.resolve()}
        photos={0}
        notes={0}
        onDone={() => onOpenChange(false)}
      />
    </>
  );
};

/**
 * "A note for Squash Bed": the same box a plant's own note is written in,
 * over the same tick list Archive all uses. Every plant starts ticked; untick
 * any that should not get the note. Save puts it on the ticked ones.
 *
 * @param props.open - Whether the panel is up.
 * @param props.onOpenChange - Opens or closes it (ignored while saving).
 * @param props.bedName - The bed's name, for the title.
 * @param props.plants - The bed's plants growing now.
 * @param props.onConfirm - Writes the note on the ticked ones; a rejection keeps the panel open with a message.
 */
export const BedNotePanel: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bedName: string;
  plants: LocationPlant[];
  onConfirm: (content: string, plantObjIds: number[]) => Promise<void>;
}> = ({ open, onOpenChange, bedName, plants, onConfirm }) => {
  const { reactTranslate: t } = useTranslation();
  const [ticked, toggle] = useTicks(plants, open);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  /** The note's box, focused once the panel has slid in and after a quick note chip. */
  const boxRef = React.useRef<HTMLTextAreaElement | null>(null);
  React.useEffect(() => {
    if (!open) return;
    setDraft('');
    setError(null);
    // After the panel has slid in, so the keyboard does not fight the animation.
    const id = window.setTimeout(() => boxRef.current?.focus(), PANEL_FOCUS_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [open]);

  const count = ticked.size;
  /** Puts the note on the ticked plants; nothing happens with no words or no plants. */
  const save = async (): Promise<void> => {
    if (!draft.trim() || count === 0) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(draft.trim(), plants.filter((p) => ticked.has(p.plant_obj_id)).map((p) => p.plant_obj_id));
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err, t('plants.notes.saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomPanel open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }} title={t('locations.bed.noteTitle', { name: bedName })} wrapTitle>
      <NoteBox
        ref={boxRef}
        value={draft}
        onChange={setDraft}
        onSave={() => { void save(); }}
        onCancel={() => onOpenChange(false)}
        busy={busy}
        saveDisabled={count === 0}
      />
      <QuickNoteChips
        onPick={(label) => { setDraft((current) => withShortcut(current, label)); boxRef.current?.focus(); }}
        disabled={busy}
        className="pt-0.5"
      />
      <p className="text-sm text-muted-foreground">
        {t(count === 1 ? 'locations.bed.noteGoes_one' : 'locations.bed.noteGoes', { count })} <span className="font-semibold text-foreground">{t('locations.bed.noteKeep')}</span>
      </p>
      <PlantTicks plants={plants} ticked={ticked} onToggle={toggle} />
      {error && <p className="text-sm text-destructive"><FailureText text={error} /></p>}
    </BottomPanel>
  );
};
